import { useEffect, useState } from 'react';
import { X, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

interface DuplicateModalProps {
  isOpen: boolean;
  /** Nombre d'opérations sélectionnées à dupliquer */
  sourceCount: number;
  onClose: () => void;
  onConfirm: (copies: number) => void;
}

const MAX_COPIES = 20;

export default function DuplicateModal({
  isOpen,
  sourceCount,
  onClose,
  onConfirm,
}: DuplicateModalProps) {
  const { t } = useTranslation('operations');
  const [copies, setCopies] = useState(1);

  // Réinitialiser à 1 à chaque ouverture
  useEffect(() => {
    if (isOpen) setCopies(1);
  }, [isOpen]);

  if (!isOpen) return null;

  const clamp = (n: number) => Math.min(MAX_COPIES, Math.max(1, n));
  const totalDrafts = copies * sourceCount;

  const handleConfirm = () => {
    onConfirm(clamp(copies));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="flex items-center gap-2 mb-2">
            <Copy className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('duplicate.title')}</h3>
          </div>
          <p className="text-gray-600 mb-5">{t('duplicate.description')}</p>

          {/* Nombre de copies */}
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('duplicate.copiesLabel')}
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCopies((c) => clamp(c - 1))}
              disabled={copies <= 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              −
            </button>
            <input
              type="number"
              min={1}
              max={MAX_COPIES}
              value={copies}
              onChange={(e) => setCopies(clamp(parseInt(e.target.value, 10) || 1))}
              className="w-20 text-center px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <button
              type="button"
              onClick={() => setCopies((c) => clamp(c + 1))}
              disabled={copies >= MAX_COPIES}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              +
            </button>
          </div>

          {sourceCount > 1 && (
            <p className="text-xs text-gray-500 mt-2">
              {t('duplicate.totalDrafts', { count: totalDrafts, sources: sourceCount })}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end mt-6">
            <Button variant="outline" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              {t('duplicate.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
